import React from "react";
import { Link } from "react-router-dom";
import Icon from "../components/AppIcon";
import { KnowtranLogo } from "../constants/images";

const TermsOfService: React.FC = () => {
  const handleEmailPress = () => {
    window.open("mailto:development@insytix.com", "_blank");
  };

  const SectionHeader = ({
    title,
    number,
  }: {
    title: string;
    number: string;
  }) => (
    <div className="mt-8 mb-4">
      <h2 className="font-jakarta font-700 text-2xl text-foreground">
        {number}. {title}
      </h2>
    </div>
  );

  const BulletPoint = ({ text }: { text: string }) => (
    <div className="flex mb-3">
      <span className="text-muted-foreground text-sm mr-3 mt-1">•</span>
      <span className="text-sm text-muted-foreground flex-1 leading-6">
        {text}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface to-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-16 h-16 flex items-center justify-center">
                <img
                  src={KnowtranLogo}
                  alt="KnowTran"
                  className="w-full h-full object-contain cursor-pointer  "
                />
              </div>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-300"
            >
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              <span className="font-inter font-600">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content - Full Width, No Background */}
      <main className="w-full px-6 lg:px-8 py-12">
        <div className="w-full">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-jakarta font-700 text-4xl lg:text-5xl text-foreground mb-4">
              Terms of Use
            </h1>
            <div className="space-y-1">
              <p className="font-inter font-500 text-muted-foreground text-lg">
                Effective Date: September 02, 2025
              </p>
              <p className="font-inter font-500 text-muted-foreground text-lg">
                Last Updated: September 02, 2025
              </p>
            </div>
          </div>

          {/* Introduction */}
          <div className="mb-6">
            <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
              Welcome to KnowTran, an Education Management System provided by
              Insytix ("Company," "we," "our," or "us"). These Terms of Use
              ("Terms") govern your access to and use of the KnowTran mobile and
              web applications (collectively, the "Services").
            </p>
            <p className="font-inter font-500 text-muted-foreground leading-7">
              By using our Services, you agree to these Terms. If you do not
              agree, please do not use the Services.
            </p>
          </div>

          {/* 1. Eligibility and Accounts */}
          <SectionHeader title="Eligibility and Accounts" number="1" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Access to KnowTran is available only to authorized students,
            teachers, staff, and administrators of institutions that have
            subscribed to the KnowTran platform.
          </p>
          <BulletPoint text="User accounts are created by authorized institute administrators only." />
          <BulletPoint text="Users are responsible for maintaining the confidentiality of their login credentials." />
          <BulletPoint text="Users may request account deletion directly from within the mobile app." />

          {/* 2. Subscription and Payments */}
          <SectionHeader title="Subscription and Payments" number="2" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Access to the KnowTran mobile app is provided as part of an
            institute's subscription to the KnowTran web platform.
          </p>
          <BulletPoint text="The mobile app itself does not handle payments, subscriptions, or purchases. All subscriptions and related payments are managed via the web platform." />
          <BulletPoint text="Any subscription terms, including pricing, billing, and renewals, will be communicated separately to subscribing institutions." />

          {/* 3. Acceptable Use */}
          <SectionHeader title="Acceptable Use" number="3" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            When using KnowTran, you agree not to:
          </p>
          <BulletPoint text="Share or transfer your account credentials with others." />
          <BulletPoint text="Upload, share, or transmit unlawful, offensive, or harmful content." />
          <BulletPoint text="Misuse or disclose confidential student or staff data without authorization." />
          <BulletPoint text="Attempt to hack, reverse-engineer, copy, or disrupt the Services." />
          <BulletPoint text="Use the Services for any activity that violates applicable laws or regulations." />

          {/* 4. Ownership and Intellectual Property */}
          <SectionHeader
            title="Ownership and Intellectual Property"
            number="4"
          />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            The KnowTran platform, including its design, software, and content
            (excluding user-provided data), is the intellectual property of
            Insytix.
          </p>
          <BulletPoint text="Users retain ownership of their personal data and institution records uploaded into the system." />
          <BulletPoint text="You may not copy, modify, or distribute any part of the Services without our prior written consent." />

          {/* 5. Privacy and Data */}
          <SectionHeader title="Privacy and Data" number="5" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            Your use of the Services is also governed by our{" "}
            <Link
              to="/privacy-policy"
              className="text-primary hover:text-primary/80 underline font-inter font-600"
            >
              Privacy Policy
            </Link>
            .
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            By using KnowTran, you agree to the collection, storage, and use of
            your information as described in the Privacy Policy.
          </p>

          {/* 6. Account Termination */}
          <SectionHeader title="Account Termination" number="6" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            We reserve the right to suspend or terminate accounts that violate
            these Terms or applicable laws.
          </p>
          <p className="font-inter font-500 text-muted-foreground leading-7">
            Users may delete their accounts at any time through the mobile app's
            account deletion feature.
          </p>

          {/* 7. Limitation of Liability */}
          <SectionHeader title="Limitation of Liability" number="7" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-4">
            The Services are provided on an "as is" and "as available" basis.
          </p>
          <BulletPoint text="Insytix is not responsible for data loss, service interruptions, or misuse of the Services by users." />
          <p className="font-inter font-500 text-muted-foreground leading-7">
            To the maximum extent permitted by law, Insytix disclaims all
            warranties and shall not be liable for any indirect, incidental, or
            consequential damages.
          </p>

          {/* 8. Governing Law */}
          <SectionHeader title="Governing Law" number="8" />
          <p className="font-inter font-500 text-muted-foreground leading-7">
            These Terms shall be governed by and construed in accordance with
            the laws of India. Any disputes shall be subject to the jurisdiction
            of courts located in Tamil Nadu, India.
          </p>

          {/* 9. Changes to Terms */}
          <SectionHeader title="Changes to Terms" number="9" />
          <p className="font-inter font-500 text-muted-foreground leading-7">
            We may update these Terms from time to time. Updates will be
            effective once posted on our website. Continued use of the Services
            after updates means you accept the revised Terms.
          </p>

          {/* 10. Contact Us */}
          <SectionHeader title="Contact Us" number="10" />
          <p className="font-inter font-500 text-muted-foreground leading-7 mb-6">
            If you have any questions about these Terms, please contact us at:
          </p>

          <div className="bg-surface/50 rounded-xl p-6 mb-8">
            <h4 className="font-jakarta font-700 text-lg text-foreground mb-4">
              Insytix
            </h4>

            <div className="space-y-4">
              <div>
                <p className="font-inter font-600 text-foreground mb-2">
                  📧 Email:
                </p>
                <button
                  onClick={handleEmailPress}
                  className="text-primary hover:text-primary/80 underline font-inter font-600"
                >
                  development@insytix.com
                </button>
              </div>

              <div>
                <p className="font-inter font-600 text-foreground mb-2">
                  📍 Address:
                </p>
                <p className="font-inter font-500 text-muted-foreground">
                  Panpozhi, Tenkasi TN - 627807, India
                </p>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <p className="font-inter font-500 text-muted-foreground text-center leading-6">
              By continuing to use KnowTran, you acknowledge that you have read,
              understood, and agree to be bound by these Terms of Use.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
